import { Button, Label, SecretInput, Spinner } from '@repo/ui'
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { toast } from 'sonner'

import { AuthShell } from '@/components/auth/auth-shell'
import { authApi } from '@/lib/api'

export const Route = createFileRoute('/redefinir-senha')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  component: ResetPasswordPage,
})

function ResetPasswordPage() {
  const navigate = useNavigate()
  const { token, error: urlError } = useSearch({ from: '/redefinir-senha' })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (urlError || !token) {
    return (
      <AuthShell
        title="Link inválido"
        subtitle="Este link expirou ou é inválido"
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <AlertTriangle className="text-destructive h-12 w-12" />
          <p className="text-muted-foreground text-sm">
            Solicite um novo link de redefinição de senha.
          </p>
          <Link
            to="/esqueci-senha"
            className="text-primary text-sm font-medium hover:underline"
          >
            Solicitar novo link
          </Link>
        </div>
      </AuthShell>
    )
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()

    if (password.length < 8) {
      toast.error('A senha deve ter no mínimo 8 caracteres')
      return
    }

    if (password !== confirm) {
      toast.error('As senhas não conferem')
      return
    }

    setSubmitting(true)

    try {
      await authApi.post('/api/auth/reset-password', {
        newPassword: password,
        token,
      })
      toast.success('Senha redefinida com sucesso!')
      await navigate({ to: '/entrar', search: { returnTo: undefined } })
    } catch {
      setSubmitting(false)
      return
    }

    setSubmitting(false)
  }

  return (
    <AuthShell
      title="Redefinir senha"
      subtitle="Defina uma nova senha para sua conta"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password">Nova senha</Label>
          <SecretInput
            id="password"
            visibilityLabel="nova senha"
            required
            autoComplete="new-password"
            placeholder="Mínimo 8 caracteres"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Confirmar senha</Label>
          <SecretInput
            id="confirm"
            visibilityLabel="confirmação da senha"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? <Spinner /> : null}
          Redefinir senha
        </Button>
      </form>
    </AuthShell>
  )
}
